import { deleteField } from 'firebase/firestore';
import type { HistoryInfoType, HistoryStepType } from '@/types/types_history';
import type { UserSimpleUidType } from '@/types/types_user';

// Retain the last N steps inline on the document; older steps are dropped from
// it. They are not lost: the `experiences_written` / `partners_written` Cloud
// Function archives a full copy of the document under each step id, and the
// history panel loads older steps back from there on demand. Same arrangement
// as the portal.
export const PRESERVE_HISTORY_STEPS = 10;

export function getStepIndex(stepKey: string): number {
  return Number(stepKey.match(/^step_(\d+)$/)![1]);
}

/**
 * The outcome of a historized write: the new history is returned synchronously
 * so the caller can put it in local state before the write resolves (rapid
 * successive edits then each see an incremented history and never collide),
 * while `saved` resolves when Firestore confirms.
 */
export interface HistorizedSaveType {
  newHistory: HistoryInfoType;
  saved: Promise<void>;
}

/**
 * Merges a new history step into a Firestore `updateObj`, mirroring the portal's
 * autosaveDocumentShared. Returns the augmented `updateObj` (with dotted
 * `history.*` field paths and deleteField() trims) plus the new `history`
 * object to keep in local state.
 */
export function applyHistoryToUpdate(
  updateObj: Record<string, unknown>,
  userAction: string,
  currentHistory: HistoryInfoType | undefined,
  userModified: UserSimpleUidType,
  isUndoStep: boolean,
): { updateObj: Record<string, unknown>; newHistory: HistoryInfoType } {
  const lastStep = currentHistory?.lastStep ?? 0;
  const newStepNum = lastStep + 1;
  const stepId = crypto.randomUUID();
  const step: HistoryStepType = {
    stepId,
    stepAction: userAction,
    dateModified: new Date().toISOString(),
    userModified,
    isUndoStep,
  };

  const out: Record<string, unknown> = { ...updateObj };
  out['history.currentStep'] = newStepNum;
  out['history.lastStep'] = newStepNum;
  out['history.currentStepId'] = stepId;
  out[`history.steps.step_${newStepNum}`] = step;

  const existingSteps = currentHistory?.steps ?? {};
  const newSteps: Record<string, HistoryStepType> = { ...existingSteps, [`step_${newStepNum}`]: step };
  for (const key of Object.keys(existingSteps)) {
    if (getStepIndex(key) < newStepNum - PRESERVE_HISTORY_STEPS) {
      out[`history.steps.${key}`] = deleteField();
      delete newSteps[key];
    }
  }

  const newHistory: HistoryInfoType = {
    currentStep: newStepNum,
    lastStep: newStepNum,
    currentStepId: stepId,
    steps: newSteps,
  };
  return { updateObj: out, newHistory };
}
